/**
 * Analytical Tree - Graph View
 * Lightweight SVG-based graph layout (no external graph library) so that
 * nodes are always visible, even if Cytoscape or other libraries misbehave.
 */

import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';

const TYPE_COLORS = {
  aircraft: '#1976d2',
  assembly: '#2e7d32',
  component: '#ed6c02',
  charter: '#9c27b0',
  sector: '#9c27b0',
  maintenance: '#d32f2f',
  amp: '#0288d1',
  adsb: '#5c6bc0',
  techlog: '#00796b'
};

const getWorstSeverity = (anomalies = []) => {
  if (!anomalies.length) return null;
  if (anomalies.some((a) => a.severity === 'critical')) return 'critical';
  if (anomalies.some((a) => a.severity === 'warning')) return 'warning';
  if (anomalies.some((a) => a.severity === 'info')) return 'info';
  return null;
};

const GraphView = ({ nodes, edges, onSelectNode }) => {
  const { positions, levels, maxDepth } = useMemo(() => {
    const nodeMap = new Map();
    (nodes || []).forEach((n) => nodeMap.set(n.id, n));

    const children = new Map();
    const hasParent = new Set();
    (edges || []).forEach((e) => {
      if (!children.has(e.from)) children.set(e.from, []);
      children.get(e.from).push(e.to);
      hasParent.add(e.to);
    });

    const roots = [];
    nodeMap.forEach((_, id) => {
      if (!hasParent.has(id)) roots.push(id);
    });
    if (!roots.length && nodes && nodes.length) {
      roots.push(nodes[0].id);
    }

    const depth = new Map();
    const queue = [];
    roots.forEach((id) => {
      depth.set(id, 0);
      queue.push(id);
    });
    while (queue.length) {
      const id = queue.shift();
      const d = depth.get(id) || 0;
      const ch = children.get(id) || [];
      ch.forEach((cid) => {
        if (!depth.has(cid)) {
          depth.set(cid, d + 1);
          queue.push(cid);
        }
      });
    }
    nodeMap.forEach((_, id) => {
      if (!depth.has(id)) depth.set(id, 0);
    });

    const levelsMap = new Map();
    depth.forEach((d, id) => {
      if (!levelsMap.has(d)) levelsMap.set(d, []);
      levelsMap.get(d).push(id);
    });

    const maxD = Math.max(0, ...depth.values());
    const width = 800;
    const height = 400;
    const marginY = 40;
    const levelHeight = maxD === 0 ? 0 : (height - 2 * marginY) / Math.max(maxD, 1);

    const pos = new Map();
    levelsMap.forEach((ids, d) => {
      const count = ids.length || 1;
      const stepX = width / (count + 1);
      ids.forEach((id, idx) => {
        const x = stepX * (idx + 1);
        const y = marginY + d * levelHeight;
        pos.set(id, { x, y });
      });
    });

    return { positions: pos, levels: levelsMap, maxDepth: maxD };
  }, [nodes, edges]);

  if (!nodes || nodes.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', minHeight: 400 }}>
        <Box component="span" sx={{ color: 'text.secondary' }}>
          Load a tree to view the graph
        </Box>
      </Paper>
    );
  }

  const width = 800;
  const height = 400;

  return (
    <Paper sx={{ p: 1, minHeight: 500 }}>
      <Typography variant="caption" color="text.secondary">
        Simple relationship graph (SVG). Click a node to view details.
      </Typography>
      <Box sx={{ mt: 1, width: '100%', height: 440 }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height="100%"
          style={{ borderRadius: 4, backgroundColor: '#fafafa' }}
        >
          {(edges || []).map((e, idx) => {
            const from = positions.get(e.from);
            const to = positions.get(e.to);
            if (!from || !to) return null;
            return (
              <line
                key={`e-${idx}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke="#ccc"
                strokeWidth={1.5}
              />
            );
          })}
          {(nodes || []).map((n) => {
            const pos = positions.get(n.id);
            if (!pos) return null;
            const worst = getWorstSeverity(n.anomalies);
            let stroke = '#666';
            if (worst === 'critical') stroke = '#d32f2f';
            else if (worst === 'warning') stroke = '#ed6c02';
            else if (worst === 'info') stroke = '#0288d1';
            const fill = TYPE_COLORS[n.type] || '#757575';
            const label = n.label || n.id;
            return (
              <g
                key={n.id}
                onClick={() => onSelectNode && onSelectNode(n)}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={16}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={2}
                />
                <text
                  x={pos.x}
                  y={pos.y - 22}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#444"
                >
                  {label.length > 18 ? `${label.slice(0, 15)}…` : label}
                </text>
                <text
                  x={pos.x}
                  y={pos.y + 30}
                  textAnchor="middle"
                  fontSize="8"
                  fill="#777"
                >
                  {n.type}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    </Paper>
  );
};

export default GraphView;

